/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import omit from 'lodash/omit';
import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { Button } from '@coursera/cds-core';
import type { ClickStatus } from '@coursera/event-pulse-types';

import Alert from 'bundles/authentication-common/components/Alert';
import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import Form from 'bundles/authentication-common/components/Form';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import SSOEnforcementBanner from 'bundles/authentication-common/components/SSOEnforcementBanner';
import Submit from 'bundles/authentication-common/components/Submit';
import * as actions from 'bundles/authentication-common/components/actions';
import { commonStyles } from 'bundles/authentication-common/components/commonStyles';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import PasswordInput from 'bundles/authentication-common/components/inputs/PasswordInput';
import TwoFactorAuthInput from 'bundles/authentication-common/components/inputs/TwoFactorAuthInput';
import {
  AUTHENTICATION_FORM_VARIANTS,
  type AUTHENTICATION_FORM_VARIANTS_KEY,
  SSO_BANNER_NAMES,
} from 'bundles/authentication-common/constants';
import {
  ARKOSE_LOGIN_KEY,
  LOGIN_REDESIGN_RECAPTCHA_ID,
  RECAPTCHA_ACTIONS,
} from 'bundles/authentication-common/constants/botManager';
import { generalErrorMessage } from 'bundles/authentication-common/constants/errors';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import type { LoginSharedProps, LoginValues } from 'bundles/authentication-common/types';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication-common/utils/loginUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);
const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = {
  form: css`
    /* This is an offset of the marginTop below to keep the space above consistent */
    margin-top: -12px;
  `,
  inputContainer: css`
    margin-top: var(--cds-spacing-150);
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
  `,
  experiment: css`
    display: flex;
    justify-content: space-between;
  `,
  item: css`
    :nth-child(1) {
      -ms-grid-row: 1;
    }

    :nth-child(2) {
      -ms-grid-row: 2;
    }

    :nth-child(3) {
      -ms-grid-row: 3;
    }

    :nth-child(4) {
      -ms-grid-row: 4;
    }

    :nth-child(5) {
      -ms-grid-row: 5;
    }
  `,
  reCAPTCHA: css`
    display: flex;
    justify-content: center;

    :nth-of-type(n) .grecaptcha-badge {
      display: none !important;
    }
  `,
  submit: css`
    margin-top: 0;
  `,
  styleConflictInputFix: css`
    :nth-of-type(n) input {
      border: 0 !important;
      height: unset;
      margin-bottom: 0 !important;
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
      line-height: 22px;
      font-size: 16px;
      outline: none;
      box-shadow: none !important;
      color: unset;
    }

    :nth-of-type(n) input:focus:required:invalid:focus {
      color: unset;
    }

    :nth-of-type(n) input::placeholder {
      color: var(--cds-color-grey-500);
      opacity: 1;
    }

    /* Internet Explorer 10-11 */
    :nth-of-type(n) input:-ms-input-placeholder {
      color: var(--cds-color-grey-500);
      opacity: 1;
    }

    /* Edge */
    :nth-of-type(n) input:-webkit-input-placeholder {
      color: var(--cds-color-grey-500);
      opacity: 1;
    }
  `,
};

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

type Context = {
  _eventData: EventData;
};

type Props = LoginSharedProps & {
  showProgressiveAuth?: boolean;
  formVariant?: AUTHENTICATION_FORM_VARIANTS_KEY;
};

export const INPUT_DEBOUNCE_TIME = 300;

const LoginForm = (props: Props, { _eventData }: Context) => {
  const { arkoseBotManagerContext, enterpriseSsoProps, showProgressiveAuth, formVariant, onEmailChange } = props;
  const {
    ssoError,
    isEnterpriseSsoLogin,
    isLoading,
    isRedirecting,
    onEmailChange: onEnterpriseSsoEmailChange,
    resetSsoError,
  } = enterpriseSsoProps || {};
  const [has2FA, setHas2FA] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<ReactNode>();
  const [emailValue, setEmailValue] = useState(props.defaultEmail || '');
  const input = useRef<HTMLInputElement>(null);
  const { trackButton } = useTrackClickButton();

  const showOnlyEmailInput = formVariant === AUTHENTICATION_FORM_VARIANTS.SOCIALSSO;
  const hidePasswordInput = showOnlyEmailInput || isEnterpriseSsoLogin;

  useEffect(() => {
    if (ssoError?.response?.prefillEmail) {
      setEmailValue(ssoError.response.prefillEmail);
      // Call both email change handlers
      onEnterpriseSsoEmailChange?.(ssoError.response.prefillEmail);
      onEmailChange?.(ssoError.response.prefillEmail);
    }
  }, [ssoError?.response?.prefillEmail, onEnterpriseSsoEmailChange, onEmailChange]);

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      context: arkoseBotManagerContext,
      publicKey: ARKOSE_LOGIN_KEY,
    },
    reCAPTCHA: {
      container: LOGIN_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.login },
    },
  });

  /* tracks click events to submit button regardless of successful or not */
  const handleTracking = (status: ClickStatus) => {
    trackButton({
      name: 'login_with_email',
      clickStatus: status,
    });
  };

  const handleError = (response: $TSFixMe) => {
    const { onError = () => {} } = props;

    resetBotManager();

    const { errorCode: code } = response?.responseJSON || {};

    if (HANDLED_ERRORS.includes(code)) {
      if (code === 'invalidCredential') {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]());
        setHas2FA(NEEDS_2FA_ERRORS.includes(code));
        setIsRunning(false);
        input.current?.focus();
      } else {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]);
        setHas2FA(NEEDS_2FA_ERRORS.includes(code));
        setIsRunning(false);
      }
    } else {
      onError(response);
    }
  };

  const login = async (values: LoginValues) => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    const { onSuccess } = props;
    if (!values.email || !values.password) {
      handleTracking('invalid');
      return setError(errorMessageFactory().invalidRequest);
    }

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      handleTracking('invalid');
      return setError(generalErrorMessage());
    }

    setIsRunning(true);

    const payload = {
      ...omit(values, 'one-time-password'),
      ...tokenData,
      ...(values['one-time-password'] && { mfa: values['one-time-password'] }),
    };

    trackAuthAttempts(_eventData, 'login');

    try {
      const response = await actions.login(payload);
      handleTracking('valid');

      return onSuccess(response?.redirectTo as string);
    } catch (error) {
      // The case where 2FA is required is treated as an error in the FE but shouldn't be for the sake of tracking
      if (!(error?.responseJSON?.errorCode === 'twoFactorRequired')) {
        handleTracking('invalid');
      }
      return handleError(error);
    }
  };

  const handleFormSubmit = (values: Record<string, string>) => {
    const loginValues = values as LoginValues;
    if (isEnterpriseSsoLogin && enterpriseSsoProps?.onSubmit) {
      return enterpriseSsoProps.onSubmit();
    }
    return login(loginValues);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetSsoError?.(); // Clear the previous sso error when the email changes
    setEmailValue(e.target.value);
    // Call enterprise SSO email change handler (for domain checking)
    onEnterpriseSsoEmailChange?.(e.target.value);
    // Call general email change handler (for magic link tracking, etc.)
    onEmailChange?.(e.target.value);
  };

  const getEnterpriseSsoError = () => {
    // If we are enforcing sso because user tried to login with social methods, show this error
    if (isLoading) {
      return null;
    }
    if (isEnterpriseSsoLogin && ssoError?.code === 'enforceSsoV2') {
      return (
        <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.SOCIAL_LOGIN} css={styles.item}>
          {_t(
            'It seems you tried to log in using social login. This email is associated with an account that requires SSO.'
          )}
        </SSOEnforcementBanner>
      );
      // If its an enterprise login because of username and password auth disabled, show this error
    } else if (isEnterpriseSsoLogin) {
      return (
        <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.PASSWORD_LOGIN} css={styles.item}>
          {showProgressiveAuth
            ? _t('This email is linked to an account that requires SSO. Please continue with SSO.')
            : _t(
                'It seems you tried to log in using username and password. This email is associated with an account that requires SSO.'
              )}
        </SSOEnforcementBanner>
      );
    }
    return null;
  };

  const renderSubmitButton = () => {
    if (showOnlyEmailInput) {
      return null;
    }
    if (isEnterpriseSsoLogin) {
      return (
        <Button disabled={isLoading} loading={isRedirecting} type="submit" style={{ minWidth: '100%' }}>
          {_t('Continue with SSO')}
        </Button>
      );
    }
    return (
      <TrackedSubmit
        css={[styles.submit, styles.item]}
        data-e2e="login-form-submit-button"
        disabled={isRunning}
        trackingName="login_form_submit_button"
      >
        {showProgressiveAuth ? _t('Next') : _t('Login')}
      </TrackedSubmit>
    );
  };

  const { onForgotPassword } = props;

  return (
    <Form className="rc-LoginForm" css={styles.form} name="login" onSubmit={handleFormSubmit}>
      <div aria-live="assertive">
        {error && !isRunning && !isEnterpriseSsoLogin && (
          <Alert css={styles.item} type="error">
            {error}
          </Alert>
        )}
        {getEnterpriseSsoError()}
      </div>

      <div css={styles.inputContainer}>
        <EmailInput
          css={[styles.styleConflictInputFix, styles.item, commonStyles.emailInputReadOnly]}
          autoFocus
          data-e2e="login-email-input"
          value={emailValue}
          ref={input}
          onChange={handleEmailChange}
          isReadOnly={!!props.defaultEmail}
        />
        {!hidePasswordInput && (
          <PasswordInput
            css={[styles.styleConflictInputFix, styles.item]}
            data-e2e="login-password-input"
            hint={
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'forgot_password' });
                    onForgotPassword();
                  };
                  return (
                    <TrackedLinkButton onClick={handleOnClick} trackingName="login_form_forgot_password_link">
                      {_t('Forgot password?')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            }
            placeholder={_t('Enter your password')}
            autoComplete="current-password"
          />
        )}
        {has2FA && (
          <TwoFactorAuthInput
            css={[styles.styleConflictInputFix, styles.item]}
            autoFocus
            data-e2e="verification-code-field"
          />
        )}
        <div css={styles.reCAPTCHA} id={LOGIN_REDESIGN_RECAPTCHA_ID} data-size="invisible" />
        {renderSubmitButton()}
      </div>
    </Form>
  );
};

LoginForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'login',
  },
}))(LoginForm);
